/**
 * Utilitário para cálculo de datas dinâmicas
 * Usado nos nodes SetVariable com value_type = 'dynamic_date'
 * 
 * RELEASE: 1.0.0 - 2025-12-28 - Criação do módulo
 */

import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfYear, 
  endOfYear, 
  addDays, 
  addMonths, 
  addYears 
} from 'date-fns';
import { DynamicDateConfig } from '@/types/flows';

/**
 * Calcula uma data dinâmica baseada na configuração fornecida
 * 
 * @param config - Configuração de data dinâmica
 * @returns String formatada da data calculada
 * 
 * @example
 * // Fim do mês atual
 * calculateDynamicDate({ base: 'end_of_month', offset_type: 'none', offset_value: 0, format: 'dd/MM/yyyy' })
 * // => "31/12/2025"
 * 
 * @example
 * // 90 dias antes do fim do mês
 * calculateDynamicDate({ base: 'end_of_month', offset_type: 'days', offset_value: -89, format: 'dd/MM/yyyy' })
 * // => "03/10/2025"
 */
export function calculateDynamicDate(config: DynamicDateConfig): string {
  // Passo 1: Define data base
  let date = new Date();
  
  switch (config.base) {
    case 'start_of_month':
      date = startOfMonth(date);
      break;
    case 'end_of_month':
      date = endOfMonth(date);
      break;
    case 'start_of_year':
      date = startOfYear(date);
      break;
    case 'end_of_year':
      date = endOfYear(date);
      break;
    // 'today' usa new Date() já definido
    case 'today':
    default:
      // Mantém a data atual
      break;
  }
  
  // Passo 2: Aplica ajuste (offset)
  if (config.offset_type !== 'none' && config.offset_value !== 0) {
    switch (config.offset_type) {
      case 'days':
        date = addDays(date, config.offset_value);
        break;
      case 'months':
        date = addMonths(date, config.offset_value);
        break;
      case 'years':
        date = addYears(date, config.offset_value);
        break;
    }
  }
  
  // Passo 3: Formata saída
  try {
    return format(date, config.format);
  } catch (error) {
    // Se formato inválido, usa padrão brasileiro
    console.warn('[calculateDynamicDate] Formato inválido, usando dd/MM/yyyy:', config.format);
    return format(date, 'dd/MM/yyyy');
  }
}

/**
 * Gera preview de uma data dinâmica para exibição na UI
 * 
 * @param config - Configuração de data dinâmica
 * @returns Objeto com data calculada e descrição legível
 */
export function previewDynamicDate(config: DynamicDateConfig): { 
  result: string; 
  description: string;
} {
  const result = calculateDynamicDate(config);
  
  // Monta descrição legível
  const baseLabels: Record<string, string> = {
    'today': 'Hoje',
    'start_of_month': 'Início do Mês',
    'end_of_month': 'Fim do Mês',
    'start_of_year': 'Início do Ano',
    'end_of_year': 'Fim do Ano',
  };
  
  const offsetLabels: Record<string, string> = {
    'days': 'dias',
    'months': 'meses',
    'years': 'anos',
  };
  
  let description = baseLabels[config.base] || config.base;
  
  if (config.offset_type !== 'none' && config.offset_value !== 0) {
    const direction = config.offset_value > 0 ? '+' : '';
    description += ` ${direction}${config.offset_value} ${offsetLabels[config.offset_type] || config.offset_type}`;
  }
  
  return { result, description };
}
